import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CommunityRole, CommunityUser } from '@/types/community-user.types';
import { useUpdateUserRoleMutation } from '@/services/CommunityUserService';
import { useSnackbar } from 'notistack';

interface UserRoleModalProps {
  user: CommunityUser | null;
  communityId: string;
  isOpen: boolean;
  onClose: () => void;
  onRoleUpdated: () => void;
}

const UserRoleModal: React.FC<UserRoleModalProps> = ({
  user,
  communityId,
  isOpen,
  onClose,
  onRoleUpdated,
}) => {
  const [selectedRole, setSelectedRole] = useState<CommunityRole | null>(
    (user?.community_role as CommunityRole) || null
  );
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [updateUserRole, { isLoading: isUpdating }] =
    useUpdateUserRoleMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Reset selected role when modal opens with a different user
  React.useEffect(() => {
    if (isOpen && user) {
      setSelectedRole(
        (user.community_role as CommunityRole) || CommunityRole.READER
      );
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value as CommunityRole);
  };

  const handleSaveRole = async () => {
    if (!selectedRole || !user || !communityId) return;

    try {
      await updateUserRole({
        community_id: communityId,
        user_id: user.id,
        role: selectedRole,
      }).unwrap();

      enqueueSnackbar(
        `Role updated successfully for ${user.name || user.email}`,
        {
          variant: 'success',
        }
      );

      // Notify parent component to refresh user data
      onRoleUpdated();

      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error updating user role:', error);
      enqueueSnackbar('Failed to update role. Please try again.', {
        variant: 'error',
      });
    }
  };

  // Get display name for the role (more user-friendly)
  const getRoleDisplayName = (role: CommunityRole): string => {
    const roleDisplayMap = {
      [CommunityRole.READER]: 'Reader - Can view content',
      [CommunityRole.CONTRIBUTOR]: 'Contributor - Can create and edit content',
      [CommunityRole.MODERATOR]: 'Moderator - Can manage content and users',
      [CommunityRole.COMMUNITY_ADMIN]:
        'Community Admin - Can manage community content',
    };

    return roleDisplayMap[role] || role;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-role-modal-title"
    >
      <div
        className="bg-gray-100 rounded-xl shadow-xl max-w-md w-full overflow-y-auto custom-scrollbar"
        role="document"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h1
              id="user-role-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              Update Community Role
            </h1>
            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
              title="Close"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                User
              </p>
              <div
                className="p-3 bg-gray-200 rounded-lg"
                role="region"
                aria-label="User information"
              >
                <p className="font-medium">{user.name || 'Unnamed User'}</p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
            </div>

            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Current Role
              </p>
              <div
                className="p-3 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 text-blue-700 dark:text-blue-200 contrast:text-blue-200 rounded-lg"
                role="status"
                aria-label="Current user role"
              >
                {getRoleDisplayName(user.community_role as CommunityRole)}
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="role-select"
                className="block text-sm font-medium text-gray-700"
              >
                New Role
                <span className="text-red-500 ml-1" aria-label="required">
                  *
                </span>
              </label>
              <div className="relative">
                <select
                  id="role-select"
                  className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-light-active appearance-none"
                  value={selectedRole || ''}
                  onChange={handleRoleChange}
                  onFocus={() => setIsSelectOpen(true)}
                  onBlur={() => setIsSelectOpen(false)}
                  disabled={isUpdating}
                  required
                  aria-required="true"
                  aria-label="Select new role"
                >
                  {Object.values(CommunityRole).map((role) => (
                    <option key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </option>
                  ))}
                </select>
                <div
                  className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                  aria-hidden="true"
                >
                  {isSelectOpen ? (
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>

            <div
              className="bg-yellow-50 dark:bg-yellow-900 contrast:bg-yellow-900 p-3 rounded-lg"
              role="note"
              aria-label="Important information"
            >
              <p className="text-sm text-yellow-700 dark:text-yellow-200 contrast:text-yellow-200">
                <span className="font-medium">Note:</span> Changing a user's
                role will modify their permissions in this community.
              </p>
            </div>
          </div>

          <div
            className="flex gap-3 mt-6"
            role="group"
            aria-label="Form actions"
          >
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isUpdating}
              aria-label="Cancel and close modal"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSaveRole}
              disabled={isUpdating || selectedRole === user.community_role}
              className={`flex-1 px-4 py-2 rounded-lg text-white ${
                isUpdating || selectedRole === user.community_role
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              aria-label={isUpdating ? 'Saving role' : 'Save role changes'}
              aria-busy={isUpdating}
            >
              {isUpdating ? 'Saving...' : 'Save Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRoleModal;
